/**
 * props.js
 * Tạo đạo cụ quán trà đá vỉa hè Hà Nội 3D: Ghế nhựa, Bàn nhựa, Ấm chén chè xanh, Biển hiệu, Chậu cây.
 */

import * as THREE from 'three';
import { CANVAS_FONTS } from '../core/fonts.js';

export function createPlasticStool(colorHex) {
  const stool = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: colorHex, roughness: 0.5, metalness: 0.05 });
  const seatGeo = new THREE.BoxGeometry(0.48, 0.05, 0.48);
  const seat = new THREE.Mesh(seatGeo, mat);
  seat.position.y = 0.34;
  seat.castShadow = true;
  seat.receiveShadow = true;
  stool.add(seat);

  const legGeo = new THREE.BoxGeometry(0.045, 0.34, 0.045);
  const offsets = [
    [-0.19, -0.19, 0.08, -0.08],
    [0.19, -0.19, -0.08, -0.08],
    [-0.19, 0.19, 0.08, 0.08],
    [0.19, 0.19, -0.08, 0.08]
  ];
  offsets.forEach(([x, z, rotX, rotZ]) => {
    const leg = new THREE.Mesh(legGeo, mat);
    leg.position.set(x, 0.17, z);
    leg.rotation.x = rotX;
    leg.rotation.z = rotZ;
    leg.castShadow = true;
    stool.add(leg);
  });
  return stool;
}

export function createTraDaSignTexture() {
  const cvs = document.createElement('canvas');
  cvs.width = 256;
  cvs.height = 384;
  const ctx = cvs.getContext('2d');
  ctx.fillStyle = '#fef08a';
  ctx.fillRect(0, 0, 256, 384);
  ctx.strokeStyle = '#b91c1c';
  ctx.lineWidth = 12;
  ctx.strokeRect(8, 8, 240, 368);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#b91c1c';
  ctx.font = `900 48px ${CANVAS_FONTS.sans}`;
  ctx.fillText('TRÀ ĐÁ', 128, 85);

  ctx.font = `700 28px ${CANVAS_FONTS.sans}`;
  ctx.fillStyle = '#1e3a8a';
  ctx.fillText('NHÂN TRẦN', 128, 148);
  ctx.fillText('KẸO LẠC', 128, 205);
  ctx.fillText('HƯỚNG DƯƠNG', 128, 262);

  ctx.fillStyle = '#dc2626';
  ctx.font = `900 32px ${CANVAS_FONTS.sans}`;
  ctx.fillText('THUỐC LÁ', 128, 335);

  const tex = new THREE.CanvasTexture(cvs);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function setupTeaStallProps(layerCenterGroup) {
  const propsGroup = new THREE.Group();
  propsGroup.name = 'Sidewalk_Props_3D';

  // Bàn nhựa trà đá
  const tableGroup = new THREE.Group();
  const tableTopMat = new THREE.MeshStandardMaterial({ color: 0x1d4ed8, roughness: 0.45 });
  const tableTopGeo = new THREE.BoxGeometry(1.4, 0.06, 0.9);
  const tableTop = new THREE.Mesh(tableTopGeo, tableTopMat);
  tableTop.position.y = 0.68;
  tableTop.castShadow = true;
  tableTop.receiveShadow = true;
  tableGroup.add(tableTop);

  // Chân bàn kim loại
  const tableLegMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.3, metalness: 0.8 });
  const tableLegGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.68, 12);
  [
    [-0.6, -0.35],
    [0.6, -0.35],
    [-0.6, 0.35],
    [0.6, 0.35]
  ].forEach(([x, z]) => {
    const leg = new THREE.Mesh(tableLegGeo, tableLegMat);
    leg.position.set(x, 0.34, z);
    leg.castShadow = true;
    tableGroup.add(leg);
  });

  // Giành tích ủ ấm chè xanh
  const potBasketGeo = new THREE.CylinderGeometry(0.18, 0.2, 0.26, 16);
  const potBasketMat = new THREE.MeshStandardMaterial({ color: 0xd97706, roughness: 0.9 });
  const potBasket = new THREE.Mesh(potBasketGeo, potBasketMat);
  potBasket.position.set(-0.35, 0.84, 0.1);
  potBasket.castShadow = true;
  tableGroup.add(potBasket);

  // Nắp ấm sứ
  const potLidGeo = new THREE.CylinderGeometry(0.1, 0.11, 0.06, 16);
  const potLidMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.2 });
  const potLid = new THREE.Mesh(potLidGeo, potLidMat);
  potLid.position.set(-0.35, 0.99, 0.1);
  potLid.castShadow = true;
  tableGroup.add(potLid);

  // Cốc trà đá thủy tinh hổ phách
  const glassGeo = new THREE.CylinderGeometry(0.075, 0.055, 0.22, 16);
  const glassMat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    transmission: 0.88,
    opacity: 0.9,
    transparent: true,
    roughness: 0.08,
    ior: 1.45
  });
  const glassMesh = new THREE.Mesh(glassGeo, glassMat);
  glassMesh.position.set(0.2, 0.82, -0.15);
  glassMesh.castShadow = true;
  tableGroup.add(glassMesh);

  // Nước trà vàng bên trong cốc
  const teaGeo = new THREE.CylinderGeometry(0.068, 0.052, 0.18, 16);
  const teaMat = new THREE.MeshStandardMaterial({ color: 0xd97706, roughness: 0.2 });
  const teaMesh = new THREE.Mesh(teaGeo, teaMat);
  teaMesh.position.set(0.2, 0.8, -0.15);
  tableGroup.add(teaMesh);

  // Đĩa hạt hướng dương
  const plateGeo = new THREE.CylinderGeometry(0.12, 0.1, 0.02, 16);
  const plateMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.3 });
  const plate = new THREE.Mesh(plateGeo, plateMat);
  plate.position.set(0.1, 0.72, 0.2);
  plate.castShadow = true;
  tableGroup.add(plate);

  tableGroup.position.set(-2.5, 0, 0.8);
  propsGroup.add(tableGroup);

  // Các ghế nhựa quanh bàn
  const stoolBlue = createPlasticStool(0x2563eb);
  stoolBlue.position.set(-2.5, 0, 1.7);
  stoolBlue.rotation.y = 0.2;
  propsGroup.add(stoolBlue);

  const stoolRed = createPlasticStool(0xdc2626);
  stoolRed.position.set(-3.5, 0, 0.8);
  stoolRed.rotation.y = 0.8;
  propsGroup.add(stoolRed);

  const stoolGreen = createPlasticStool(0x16a34a);
  stoolGreen.position.set(-1.5, 0, 0.9);
  stoolGreen.rotation.y = -0.5;
  propsGroup.add(stoolGreen);

  // Biển hiệu vỉa hè "TRÀ ĐÁ - NHÂN TRẦN"
  const signBoardGeo = new THREE.BoxGeometry(0.7, 1.0, 0.04);
  const signBoardMat = new THREE.MeshStandardMaterial({
    map: createTraDaSignTexture(),
    roughness: 0.8
  });
  const signBoardMesh = new THREE.Mesh(signBoardGeo, signBoardMat);
  signBoardMesh.position.set(-4.2, 0.5, -0.6);
  signBoardMesh.rotation.y = 0.25;
  signBoardMesh.rotation.x = -0.08;
  signBoardMesh.castShadow = true;
  propsGroup.add(signBoardMesh);

  // Chậu cây cảnh mini bên phải
  const potGeo = new THREE.CylinderGeometry(0.35, 0.25, 0.55, 16);
  const potMat = new THREE.MeshStandardMaterial({ color: 0xb45309, roughness: 0.85 });
  const pot = new THREE.Mesh(potGeo, potMat);
  pot.position.set(3.4, 0.27, -0.4);
  pot.castShadow = true;
  pot.receiveShadow = true;
  propsGroup.add(pot);

  const foliageGeo = new THREE.DodecahedronGeometry(0.55, 1);
  const foliageMat = new THREE.MeshStandardMaterial({ color: 0x15803d, roughness: 0.7 });
  const foliage = new THREE.Mesh(foliageGeo, foliageMat);
  foliage.position.set(3.4, 0.95, -0.4);
  foliage.castShadow = true;
  propsGroup.add(foliage);

  layerCenterGroup.add(propsGroup);
  return propsGroup;
}

/**
 * Tạo cột điện đô thị / đạo cụ khe hẻm che mép cắt giữa các module nhà
 */
export function createSeamCoverPole() {
  const poleGroup = new THREE.Group();
  poleGroup.name = 'ModularSeam_Cover_Pole';

  const concreteMat = new THREE.MeshStandardMaterial({
    color: 0x94a3b8,
    roughness: 0.85,
    metalness: 0.08
  });
  const darkMetalMat = new THREE.MeshStandardMaterial({
    color: 0x334155,
    roughness: 0.45,
    metalness: 0.6
  });

  // 1. Thân cột bê tông ly tâm (cao 15.5m bao phủ trọn vẹn toàn bộ chiều cao mép nhà từ sàn lên mái)
  const poleGeo = new THREE.CylinderGeometry(0.15, 0.2, 15.5, 16);
  const poleMesh = new THREE.Mesh(poleGeo, concreteMat);
  poleMesh.position.y = 7.75;
  poleMesh.castShadow = true;
  poleMesh.receiveShadow = true;
  poleGroup.add(poleMesh);

  // Đế cột bê tông vuông
  const baseGeo = new THREE.BoxGeometry(0.5, 0.4, 0.5);
  const baseMesh = new THREE.Mesh(baseGeo, concreteMat);
  baseMesh.position.y = 0.2;
  baseMesh.castShadow = true;
  baseMesh.receiveShadow = true;
  poleGroup.add(baseMesh);

  // 2. Xà đỡ ngang (crossarms)
  const armGeo1 = new THREE.BoxGeometry(1.6, 0.08, 0.08);
  const arm1 = new THREE.Mesh(armGeo1, darkMetalMat);
  arm1.position.set(0, 12.6, 0);
  arm1.castShadow = true;
  poleGroup.add(arm1);

  const armGeo2 = new THREE.BoxGeometry(1.3, 0.08, 0.08);
  const arm2 = new THREE.Mesh(armGeo2, darkMetalMat);
  arm2.position.set(0, 10.8, 0);
  arm2.castShadow = true;
  poleGroup.add(arm2);

  // Sứ cách điện (Insulators)
  const insulMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.2 });
  const insulGeo = new THREE.CylinderGeometry(0.04, 0.06, 0.18, 10);
  [-0.65, 0, 0.65].forEach((xOff) => {
    const ins = new THREE.Mesh(insulGeo, insulMat);
    ins.position.set(xOff, 12.73, 0);
    ins.castShadow = true;
    poleGroup.add(ins);
  });
  [-0.5, 0.5].forEach((xOff) => {
    const ins = new THREE.Mesh(insulGeo, insulMat);
    ins.position.set(xOff, 10.93, 0);
    ins.castShadow = true;
    poleGroup.add(ins);
  });

  // 3. Hộp công tơ điện
  const boxGeo = new THREE.BoxGeometry(0.38, 0.68, 0.26);
  const boxMesh = new THREE.Mesh(boxGeo, darkMetalMat);
  boxMesh.position.set(0.02, 2.3, 0.12);
  boxMesh.castShadow = true;
  poleGroup.add(boxMesh);

  // Biển báo số ngõ "NGÕ 24 PHỐ CỔ"
  const cvs = document.createElement('canvas');
  cvs.width = 192;
  cvs.height = 96;
  const ctx = cvs.getContext('2d');
  ctx.fillStyle = '#1e3a8a';
  ctx.fillRect(0, 0, 192, 96);
  ctx.strokeStyle = '#f8fafc';
  ctx.lineWidth = 4;
  ctx.strokeRect(4, 4, 184, 88);
  ctx.fillStyle = '#f8fafc';
  ctx.textAlign = 'center';
  ctx.font = `800 30px ${CANVAS_FONTS.sans}`;
  ctx.fillText('NGÕ 24', 96, 42);
  ctx.font = `600 18px ${CANVAS_FONTS.sans}`;
  ctx.fillText('PHỐ CỔ', 96, 74);
  const signTex = new THREE.CanvasTexture(cvs);
  signTex.colorSpace = THREE.SRGBColorSpace;
  const signGeo = new THREE.BoxGeometry(0.54, 0.27, 0.03);
  const signMat = new THREE.MeshStandardMaterial({ map: signTex, roughness: 0.5 });
  const signMesh = new THREE.Mesh(signGeo, signMat);
  signMesh.position.set(0, 3.4, 0.18);
  signMesh.castShadow = true;
  poleGroup.add(signMesh);

  // 4. Bó dây cáp viễn thông
  const cableMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.9 });
  const cableGeo = new THREE.TorusGeometry(0.24, 0.045, 8, 20);
  const cableRing = new THREE.Mesh(cableGeo, cableMat);
  cableRing.position.set(0, 4.2, 0.12);
  cableRing.rotation.x = Math.PI / 2;
  cableRing.castShadow = true;
  poleGroup.add(cableRing);

  // Dây cáp đứng chạy dọc thân cột
  const dropCableGeo = new THREE.CylinderGeometry(0.02, 0.02, 6.2, 8);
  const dropCable = new THREE.Mesh(dropCableGeo, cableMat);
  dropCable.position.set(0.13, 6.0, 0.09);
  poleGroup.add(dropCable);

  return poleGroup;
}
